import { IonContent, IonLoading, IonPage } from '@ionic/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import Invalid from '@/components/common/invalid/Invalid';
import Loading from '@/components/common/loading/Loading';
import Logo from '@/components/common/logo/Logo';
import Padding from '@/components/common/padding/Padding';
import ProgressBar from '@/components/common/progressBar/ProgressBar';
import Confirmation from '@/components/scheduleFlow/confirmation/Confirmation';
import SelectDate from '@/components/scheduleFlow/selectDate/SelectDate';
import SelectDentist from '@/components/scheduleFlow/selectDentist/SelectDentist';
import SelectPurpose from '@/components/scheduleFlow/selectPurpose/SelectPurpose';
import SelectTime from '@/components/scheduleFlow/selectTime/SelectTime';
import SignUp from '@/components/scheduleFlow/signUp/SignUp';
import ThankYou from '@/components/scheduleFlow/thankYou/ThankYou';
import Verification from '@/components/scheduleFlow/verification/Verification';
import {
  ScheduleFlowProvider,
  useScheduleFlow
} from '@/contexts/ScheduleFlowContext';
import { SCHEDULE_FLOW_STEPS } from '@/utils/constants';

import './Schedule.css';

const Schedule: React.FC = () => {
  const { loading, clinic, currentStep, setCurrentStep, selectedDentist } =
    useScheduleFlow();

  const [presentingElement, setPresentingElement] = useState<
    HTMLElement | undefined
  >(undefined);

  const page = useRef<HTMLElement | undefined>(undefined);

  useEffect(() => {
    setPresentingElement(page.current);
  }, [page.current]);

  const showBackButton = [
    SCHEDULE_FLOW_STEPS.SELECT_PURPOSE,
    SCHEDULE_FLOW_STEPS.SELECT_DATE,
    SCHEDULE_FLOW_STEPS.SELECT_TIME,
    SCHEDULE_FLOW_STEPS.SIGN_UP,
    SCHEDULE_FLOW_STEPS.VERIFICATION,
    SCHEDULE_FLOW_STEPS.CONFIRMATION
  ].includes(currentStep);

  const onBack = () => {
    const previousStepIndex =
      Object.values(SCHEDULE_FLOW_STEPS).indexOf(currentStep) -
      (currentStep === SCHEDULE_FLOW_STEPS.CONFIRMATION &&
      !selectedDentist?.patient_verification_enabled
        ? 2
        : 1);
    const newStep = Object.values(SCHEDULE_FLOW_STEPS)[previousStepIndex];
    if (newStep) setCurrentStep(newStep);
  };

  const renderScheduleFlow = () => {
    const stepComponents: { [key: string]: JSX.Element } =
      selectedDentist?.patient_verification_enabled
        ? {
            [SCHEDULE_FLOW_STEPS.SELECT_DENTIST]: <SelectDentist />,
            [SCHEDULE_FLOW_STEPS.SELECT_PURPOSE]: (
              <SelectPurpose presentingElement={presentingElement} />
            ),
            [SCHEDULE_FLOW_STEPS.SELECT_DATE]: <SelectDate />,
            [SCHEDULE_FLOW_STEPS.SELECT_TIME]: <SelectTime />,
            [SCHEDULE_FLOW_STEPS.SIGN_UP]: <SignUp />,
            [SCHEDULE_FLOW_STEPS.VERIFICATION]: <Verification />,
            [SCHEDULE_FLOW_STEPS.CONFIRMATION]: <Confirmation />,
            [SCHEDULE_FLOW_STEPS.THANK_YOU]: <ThankYou />
          }
        : {
            [SCHEDULE_FLOW_STEPS.SELECT_DENTIST]: <SelectDentist />,
            [SCHEDULE_FLOW_STEPS.SELECT_PURPOSE]: (
              <SelectPurpose presentingElement={presentingElement} />
            ),
            [SCHEDULE_FLOW_STEPS.SELECT_DATE]: <SelectDate />,
            [SCHEDULE_FLOW_STEPS.SELECT_TIME]: <SelectTime />,
            [SCHEDULE_FLOW_STEPS.SIGN_UP]: <SignUp />,
            [SCHEDULE_FLOW_STEPS.CONFIRMATION]: <Confirmation />,
            [SCHEDULE_FLOW_STEPS.THANK_YOU]: <ThankYou />
          };

    const totalSteps = Object.keys(stepComponents).length - 2;
    const currentStepIndex =
      currentStep === SCHEDULE_FLOW_STEPS.THANK_YOU
        ? -1
        : Object.keys(stepComponents).indexOf(currentStep) - 1;

    return (
      <>
        <ProgressBar
          totalSteps={totalSteps}
          currentProgress={currentStepIndex}
        />
        {stepComponents[currentStep]}
      </>
    );
  };

  return (
    <IonPage id="schedule-page" ref={page}>
      <IonContent>
        <Padding>
          <Logo showBackButton={showBackButton} onBack={onBack} />
          {clinic ? renderScheduleFlow() : loading ? <Loading /> : <Invalid />}
        </Padding>
      </IonContent>

      <IonLoading
        isOpen={!!clinic && loading}
        message="Loading..."
        spinner="crescent"
      />
    </IonPage>
  );
};

const ScheduleWithProvider: React.FC = () => (
  <ScheduleFlowProvider>
    <Schedule />
  </ScheduleFlowProvider>
);

export default ScheduleWithProvider;
